import React, { useMemo } from 'react';
import styled from 'styled-components/macro';
import scaleSize from 'utils/scale-size';
import { LiveEventType } from 'store/pages/loaders/loader-types';

const HALF_OF_HOUR = 30 * 60 * 1000;
const THRESHOLD_IN_PIXELS = scaleSize(400);

interface IStyledProgramWrapper {
  width: number;
}

export const StyledProgramWrapper = styled.div.attrs<IStyledProgramWrapper>(({ width }) => {
  return { style: { width: `${width}px` } };
})<IStyledProgramWrapper>`
  position: relative;
  display: inline-block;
  box-sizing: border-box;
  height: ${scaleSize(120)}px;
  width: ${scaleSize(400)}px;
  padding-top: ${scaleSize(50)}px;
  padding-left: ${scaleSize(40)}px;
  border: 2px solid rgba(255, 255, 255, 0.05);
  margin-right: ${scaleSize(5)}px;
  background: #26262c;

  &.no-content {
    display: none;
    background: linear-gradient(270deg, #18181b 64.75%, #18181b 82.39%);
  }

  &.live {
    background: linear-gradient(270deg, #18181b 64.75%, #18181b 82.39%);
  }
`;

export const StyledProgramTitle = styled.div`
  font-size: ${scaleSize(30)}px;
  line-height: ${scaleSize(30)}px;
  margin-bottom: ${scaleSize(10)}px;
  font-family: 'RobotoRegular';
  font-style: normal;
  font-weight: 500;
  letter-spacing: -0.236111px;
  color: ${({ theme }) => theme.defaultColor};
  mix-blend-mode: normal;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const StyledProgramMeta = styled.div`
  display: none;
  line-height: ${scaleSize(30)}px;
  font-family: 'RobotoRegular';
  font-style: normal;
  font-weight: 500;
  font-size: ${scaleSize(24)}px;
  letter-spacing: -0.236111px;
  color: ${({ theme }) => theme.epgTimeColor};
  opacity: 0.5;
`;

interface IProgramItem {
  item: LiveEventType;
  currentTime: number;
  timeSheetStartTime: number;
  channelName: string;
}

type StaticComponents = {
  NO_CONTENT: React.FC;
};

const ProgramItem: React.FC<IProgramItem> & StaticComponents = (props) => {
  const { item, channelName, currentTime, timeSheetStartTime } = props;
  const startTimeMs = new Date(item.start_time).getTime();
  const endTimeMs = new Date(item.end_time).getTime();

  const width = useMemo(() => {
    const durationMs = endTimeMs - startTimeMs;
    const timeLeft = endTimeMs - timeSheetStartTime;
    const durationLeftMs = Math.min(timeLeft, durationMs);
    return (THRESHOLD_IN_PIXELS * durationLeftMs) / HALF_OF_HOUR;
  }, [endTimeMs, startTimeMs, timeSheetStartTime]);

  const time = useMemo(() => {
    const s = new Date(startTimeMs);
    const e = new Date(endTimeMs);

    const sHours = `${s.getHours()}`;
    const sMinutes = `0${s.getMinutes()}`.slice(-2);
    const eHours = `${e.getHours()}`;
    const eMinutes = `0${e.getMinutes()}`.slice(-2);

    return `${sHours}:${sMinutes} - ${eHours}:${eMinutes}`;
  }, [endTimeMs, startTimeMs]);
  const isLive = useMemo(() => {
    return startTimeMs <= currentTime && endTimeMs > currentTime;
  }, [currentTime, endTimeMs, startTimeMs]);

  return (
    <StyledProgramWrapper className={isLive ? 'live' : ''} width={width}>
      <StyledProgramTitle>{item.name}</StyledProgramTitle>
      <StyledProgramMeta>
        {time} / {channelName}
      </StyledProgramMeta>
    </StyledProgramWrapper>
  );
};

ProgramItem.NO_CONTENT = () => {
  return (
    <StyledProgramWrapper className={'no-content'} width={0}>
      <StyledProgramTitle>No content available</StyledProgramTitle>
    </StyledProgramWrapper>
  );
};

export default ProgramItem;
